
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { v4 as uuidv4 } from 'uuid'
import GameRankingBoard from '../components/GameRankingBoard'
import FinalRankingBoard from '../components/FinalRankingBoard'
import QuizResultsPanel from '../components/QuizResultsPanel'
import { io } from 'socket.io-client'
import QRCode from 'qrcode'

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
  max-width: 2400px;
  margin: 0 auto;
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.3) 50%, transparent 50%),
    linear-gradient(90deg, rgba(0, 0, 0, 0.3) 50%, transparent 50%),
    linear-gradient(135deg, #0a0015 0%, #1a0033 50%, #0f001f 100%);
  background-size: 4px 4px, 4px 4px, 100% 100%;
  background-position: 0 0, 0 0, 0 0;
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 40px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  /* CRT 스캔라인 효과 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.15) 0px,
      transparent 1px,
      transparent 2px,
      rgba(0, 0, 0, 0.15) 3px
    );
    pointer-events: none;
    z-index: 999;
    animation: scanlines 8s linear infinite;
  }

  /* 네온 글로우 */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(255, 0, 255, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 80% 70%, rgba(0, 255, 255, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 40% 80%, rgba(255, 255, 0, 0.1) 0%, transparent 25%);
    animation: neonPulse 3s ease-in-out infinite;
    pointer-events: none;
    mix-blend-mode: screen;
  }

  @keyframes scanlines {
    0% { transform: translateY(0); }
    100% { transform: translateY(4px); }
  }

  @keyframes neonPulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }
`

const Header = styled.div`
  position: absolute;
  top: 30px;
  left: 40px;
  z-index: 10;
  animation: slideInLeft 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);

  @keyframes slideInLeft {
    0% { opacity: 0; transform: translateX(-100px) rotate(-10deg); }
    100% { opacity: 1; transform: translateX(0) rotate(0deg); }
  }

  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
    filter: drop-shadow(0 4px 20px rgba(78, 205, 196, 0.5));
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    animation: floatLogo 4s ease-in-out infinite;

    &:hover {
      transform: scale(1.15) rotate(5deg);
      filter: drop-shadow(0 8px 30px rgba(78, 205, 196, 0.8));
    }
  }

  @keyframes floatLogo {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
`

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 35px;
  margin-top: 60px;
  z-index: 1;
  animation: fadeInUp 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;

  @keyframes fadeInUp {
    0% { opacity: 0; transform: translateY(50px); }
    100% { opacity: 1; transform: translateY(0); }
  }

  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: stretch;
  animation: slideInLeft 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s backwards;

  @keyframes slideInLeft {
    0% { opacity: 0; transform: translateX(-50px); }
    100% { opacity: 1; transform: translateX(0); }
  }
`

const RoomCodeDisplay = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 4px solid #00ffff;
  border-radius: 8px;
  padding: 32px 28px;
  text-align: center;
  box-shadow:
    0 0 30px rgba(0, 255, 255, 0.6),
    inset 0 0 30px rgba(0, 255, 255, 0.1),
    0 8px 32px rgba(0, 0, 0, 0.8);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  /* 픽셀 도트 패턴 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle, rgba(0, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 8px 8px;
    animation: pixelMove 20s linear infinite;
  }

  /* 네온 깜빡임 */
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 2px solid #ff00ff;
    border-radius: 8px;
    opacity: 0;
    animation: neonBlink 2s ease-in-out infinite;
  }

  @keyframes pixelMove {
    0% { transform: translate(0, 0); }
    100% { transform: translate(8px, 8px); }
  }

  @keyframes neonBlink {
    0%, 100% { opacity: 0; }
    50% { opacity: 0.5; box-shadow: 0 0 20px #ff00ff; }
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 0 40px rgba(0, 255, 255, 0.9),
      inset 0 0 40px rgba(0, 255, 255, 0.2),
      0 12px 48px rgba(0, 0, 0, 0.9);
    border-color: #00ffff;
  }
`

const RoomCodeTitle = styled.h1`
  font-size: 16px;
  font-weight: 900;
  color: #ffff00;
  margin: 0 0 12px 0;
  letter-spacing: 4px;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  text-shadow:
    0 0 10px #ffff00,
    0 0 20px #ffff00,
    0 0 30px #ffff00,
    2px 2px 0px rgba(0, 0, 0, 0.8);
  animation: textBlink 1.5s ease-in-out infinite;

  @keyframes textBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`

const RoomCodeNumber = styled.h2`
  font-size: 56px;
  font-weight: 900;
  color: #00ffff;
  margin: 0;
  letter-spacing: 12px;
  font-family: 'Courier New', monospace;
  position: relative;
  text-shadow:
    0 0 10px #00ffff,
    0 0 20px #00ffff,
    0 0 30px #00ffff,
    0 0 40px #00ffff,
    0 0 70px #00ffff,
    0 0 80px #00ffff,
    3px 3px 0px rgba(0, 0, 0, 0.9);
  animation: neonFlicker 3s ease-in-out infinite;

  @keyframes neonFlicker {
    0%, 18%, 22%, 25%, 53%, 57%, 100% {
      text-shadow:
        0 0 10px #00ffff,
        0 0 20px #00ffff,
        0 0 30px #00ffff,
        0 0 40px #00ffff,
        0 0 70px #00ffff,
        0 0 80px #00ffff,
        3px 3px 0px rgba(0, 0, 0, 0.9);
    }
    20%, 24%, 55% {
      text-shadow:
        0 0 5px #00ffff,
        3px 3px 0px rgba(0, 0, 0, 0.9);
    }
  }
`

const RoomCodeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  position: relative;
  z-index: 10; /* RoomCodeDisplay의 pseudo-elements 위에 표시 */
`

const CopyButton = styled.button`
  background: rgba(0, 255, 255, 0.2);
  border: 2px solid #00ffff;
  border-radius: 6px;
  color: #00ffff;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
  font-weight: 700;
  transition: all 0.3s ease;
  box-shadow: 0 0 10px rgba(0, 255, 255, 0.3);
  position: relative;
  z-index: 20; /* 확실하게 클릭 가능하도록 */

  &:hover {
    background: rgba(0, 255, 255, 0.4);
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.6);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 0 5px rgba(0, 255, 255, 0.3);
  }

  &.copied {
    background: rgba(0, 255, 0, 0.3);
    border-color: #00ff00;
    color: #00ff00;
    animation: copySuccess 0.5s ease;
  }

  @keyframes copySuccess {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`

const ControlPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const GameButton = styled.button`
  background: ${p => p.$canStart
    ? 'rgba(255, 0, 255, 0.3)'
    : 'rgba(80, 80, 80, 0.3)'};
  color: ${p => p.$canStart ? '#ff00ff' : '#666'};
  border: 4px solid ${p => p.$canStart ? '#ff00ff' : '#444'};
  border-radius: 0;
  padding: 20px 32px;
  font-size: 20px;
  font-weight: 900;
  font-family: 'Courier New', monospace;
  cursor: ${p => (p.$canStart ? 'pointer' : 'not-allowed')};
  transition: all 0.2s ease;
  box-shadow: ${p => p.$canStart
    ? '0 0 20px rgba(255, 0, 255, 0.6), inset 0 0 20px rgba(255, 0, 255, 0.2), 0 8px 32px rgba(0, 0, 0, 0.8)'
    : '0 4px 16px rgba(0, 0, 0, 0.5)'};
  letter-spacing: 3px;
  text-transform: uppercase;
  position: relative;
  width: 100%;
  text-shadow: ${p => p.$canStart
    ? '0 0 10px #ff00ff, 0 0 20px #ff00ff, 2px 2px 0px rgba(0, 0, 0, 0.9)'
    : '2px 2px 0px rgba(0, 0, 0, 0.5)'};

  animation: ${p => p.$canStart ? 'arcadeButtonPulse 1s ease-in-out infinite' : 'none'};

  /* 픽셀 도트 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle, rgba(255, 0, 255, 0.2) 1px, transparent 1px);
    background-size: 6px 6px;
    opacity: ${p => p.$canStart ? 1 : 0.3};
  }

  @keyframes arcadeButtonPulse {
    0%, 100% {
      box-shadow:
        0 0 20px rgba(255, 0, 255, 0.6),
        inset 0 0 20px rgba(255, 0, 255, 0.2),
        0 8px 32px rgba(0, 0, 0, 0.8);
    }
    50% {
      box-shadow:
        0 0 40px rgba(255, 0, 255, 0.9),
        inset 0 0 30px rgba(255, 0, 255, 0.4),
        0 12px 40px rgba(0, 0, 0, 0.9);
      transform: translateY(-2px);
    }
  }

  &:hover {
    transform: ${p => (p.$canStart ? 'translateY(-4px) scale(1.02)' : 'none')};
    box-shadow: ${p => p.$canStart
      ? '0 0 40px rgba(255, 0, 255, 1), inset 0 0 30px rgba(255, 0, 255, 0.4), 0 12px 48px rgba(0, 0, 0, 0.9)'
      : '0 4px 16px rgba(0, 0, 0, 0.5)'};
  }

  &:active {
    transform: ${p => (p.$canStart ? 'translateY(0) scale(0.98)' : 'none')};
  }
`

const QRButton = styled.button`
  background: rgba(0, 255, 255, 0.2);
  color: #00ffff;
  border: 3px solid #00ffff;
  border-radius: 0;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 900;
  font-family: 'Courier New', monospace;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow:
    0 0 15px rgba(0, 255, 255, 0.5),
    inset 0 0 15px rgba(0, 255, 255, 0.1),
    0 6px 24px rgba(0, 0, 0, 0.7);
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  width: 100%;
  text-shadow:
    0 0 8px #00ffff,
    0 0 15px #00ffff,
    2px 2px 0px rgba(0, 0, 0, 0.8);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle, rgba(0, 255, 255, 0.15) 1px, transparent 1px);
    background-size: 6px 6px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow:
      0 0 25px rgba(0, 255, 255, 0.8),
      inset 0 0 20px rgba(0, 255, 255, 0.2),
      0 8px 32px rgba(0, 0, 0, 0.8);
    background: rgba(0, 255, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`

const RightSection = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 25px;
  animation: slideInRight 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s backwards;

  @keyframes slideInRight {
    0% { opacity: 0; transform: translateX(50px); }
    100% { opacity: 1; transform: translateX(0); }
  }
`

const PlayersContainer = styled.div`
  background: rgba(0, 0, 0, 0.7);
  border: 4px solid #00ff00;
  border-radius: 0;
  padding: 24px;
  box-shadow:
    0 0 30px rgba(0, 255, 0, 0.6),
    inset 0 0 30px rgba(0, 255, 0, 0.1),
    0 8px 32px rgba(0, 0, 0, 0.8);
  position: relative;
  overflow: hidden;

  /* 픽셀 그리드 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle, rgba(0, 255, 0, 0.08) 1px, transparent 1px);
    background-size: 8px 8px;
    animation: pixelMove 20s linear infinite;
  }

  @keyframes pixelMove {
    0% { transform: translate(0, 0); }
    100% { transform: translate(8px, 8px); }
  }
`

const PlayersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 3px solid #00ff00;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100%;
    height: 3px;
    background: #00ff00;
    box-shadow: 0 0 15px #00ff00;
    animation: borderPulse 1.5s ease-in-out infinite;
  }

  @keyframes borderPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
`

const PlayersTitle = styled.h3`
  font-size: 22px;
  font-weight: 900;
  color: #00ff00;
  margin: 0;
  letter-spacing: 3px;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;
  text-shadow:
    0 0 10px #00ff00,
    0 0 20px #00ff00,
    0 0 30px #00ff00,
    2px 2px 0px rgba(0, 0, 0, 0.9);
  animation: titleFlicker 2s ease-in-out infinite;

  @keyframes titleFlicker {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
`

const PlayersCount = styled.span`
  background: rgba(255, 255, 0, 0.3);
  color: #ffff00;
  padding: 8px 20px;
  border: 3px solid #ffff00;
  border-radius: 0;
  font-size: 18px;
  font-weight: 900;
  font-family: 'Courier New', monospace;
  box-shadow:
    0 0 20px rgba(255, 255, 0, 0.6),
    inset 0 0 20px rgba(255, 255, 0, 0.2);
  text-shadow:
    0 0 10px #ffff00,
    0 0 20px #ffff00,
    2px 2px 0px rgba(0, 0, 0, 0.9);
  animation: countBlink 1s ease-in-out infinite;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle, rgba(255, 255, 0, 0.15) 1px, transparent 1px);
    background-size: 4px 4px;
  }

  @keyframes countBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.85; }
  }
`

const PlayersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 20px;
  width: 100%;
`

const PlayerSlot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 12px;
  border-radius: 0;
  background: ${p => (p.$hasPlayer
    ? 'rgba(255, 0, 255, 0.2)'
    : 'rgba(255, 255, 255, 0.05)')};
  border: 3px solid ${p => (p.$hasPlayer ? '#ff00ff' : 'rgba(255, 255, 255, 0.2)')};
  transition: all 0.3s ease;
  box-shadow: ${p => (p.$hasPlayer
    ? '0 0 20px rgba(255, 0, 255, 0.5), inset 0 0 20px rgba(255, 0, 255, 0.1)'
    : 'none')};
  position: relative;
  overflow: hidden;

  /* 픽셀 도트 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(circle, ${p => (p.$hasPlayer
        ? 'rgba(255, 0, 255, 0.15)'
        : 'rgba(255, 255, 255, 0.05)')} 1px, transparent 1px);
    background-size: 4px 4px;
  }

  &:hover {
    transform: ${p => (p.$hasPlayer ? 'translateY(-4px) scale(1.05)' : 'none')};
    border-color: ${p => (p.$hasPlayer ? '#ff00ff' : 'rgba(255, 255, 255, 0.2)')};
    box-shadow: ${p => (p.$hasPlayer
      ? '0 0 30px rgba(255, 0, 255, 0.8), inset 0 0 30px rgba(255, 0, 255, 0.2)'
      : 'none')};
  }
`

const PlayerAvatar = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 0;
  object-fit: cover;
  border: 3px solid #ff00ff;
  box-shadow:
    0 0 15px rgba(255, 0, 255, 0.5),
    inset 0 0 15px rgba(255, 0, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  filter: brightness(1) contrast(1.1);
  image-rendering: pixelated;

  ${PlayerSlot}:hover & {
    transform: scale(1.1);
    border-color: #ff00ff;
    box-shadow:
      0 0 25px rgba(255, 0, 255, 0.8),
      inset 0 0 20px rgba(255, 0, 255, 0.2);
    filter: brightness(1.15) contrast(1.2);
  }
`

const PlayerInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
`

const PlayerName = styled.span`
  font-size: 13px;
  font-weight: 900;
  color: #ff00ff;
  text-align: center;
  letter-spacing: 1px;
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
  text-shadow:
    0 0 8px #ff00ff,
    0 0 15px #ff00ff,
    2px 2px 0px rgba(0, 0, 0, 0.9);
  transition: all 0.3s ease;

  ${PlayerSlot}:hover & {
    color: #ffff00;
    text-shadow:
      0 0 10px #ffff00,
      0 0 20px #ffff00,
      2px 2px 0px rgba(0, 0, 0, 0.9);
    transform: scale(1.08);
  }
`

const PlayerMeta = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  word-break: break-all;
`

const EmptySlot = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: rgba(255, 255, 255, 0.2);
  border: 2px dashed rgba(255, 255, 255, 0.1);
`

const WaitingText = styled.p`
  text-align: center;
  font-size: 16px;
  color: #00ff00;
  margin: 0 0 16px 0;
  line-height: 1.5;
  letter-spacing: 1.5px;
  font-weight: 900;
  font-family: 'Courier New', monospace;
  text-transform: uppercase;
  text-shadow:
    0 0 10px #00ff00,
    0 0 20px #00ff00,
    2px 2px 0px rgba(0, 0, 0, 0.9);
  animation: waitingBlink 1.5s ease-in-out infinite;

  @keyframes waitingBlink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`

const ObjectQrSection = styled.section`
  width: 100%;
  max-width: 1400px;
  margin: 30px auto 0;
  padding: 30px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.08));
  backdrop-filter: blur(15px);
  border: 2px solid rgba(78, 205, 196, 0.4);
  border-radius: 25px;
  box-shadow:
    0 15px 45px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    0 0 40px rgba(78, 205, 196, 0.2);
  display: flex;
  flex-direction: column;
  gap: 25px;
  z-index: 1;
`

const ObjectQrHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.8;
    transform: translateY(-2px);
  }
`

const ToggleIcon = styled.span`
  font-size: 24px;
  color: #4ECDC4;
  transition: transform 0.3s ease;
  transform: rotate(${p => p.$isExpanded ? '180deg' : '0deg'});
`

const ObjectQrTitle = styled.h3`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #4ECDC4;
  text-align: center;
  letter-spacing: 1px;
`

const ObjectQrSubtitle = styled.p`
  margin: 0;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  line-height: 1.6;
`

const ObjectQrGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 25px;
`

const ObjectQrCard = styled.div`
  background: linear-gradient(145deg, rgba(78, 205, 196, 0.1), rgba(68, 160, 141, 0.05));
  border-radius: 20px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  border: 2px solid rgba(78, 205, 196, 0.3);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: rgba(78, 205, 196, 0.5);
    box-shadow: 0 12px 35px rgba(78, 205, 196, 0.3);
  }

  img {
    width: 130px;
    height: 130px;
  }
`

const ObjectPreview = styled.div`
  width: 130px;
  height: 130px;
  border-radius: 15px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(78, 205, 196, 0.2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ObjectPreviewFallback = styled.span`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  padding: 0 10px;
  text-align: center;
`

const ObjectQrName = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`

const ObjectQrPayload = styled.code`
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 11px;
  word-break: break-word;
  text-align: center;
  width: 100%;
  border: 1px solid rgba(78, 205, 196, 0.2);
  font-family: 'Courier New', monospace;
`

const LogPanel = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 500px;
  max-height: 500px;
  background: rgba(0, 0, 0, 0.95);
  border-radius: 12px;
  border: 1px solid rgba(78, 205, 196, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 9999;
`

const LogHeader = styled.div`
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LogTitle = styled.div`
  color: #4ECDC4;
  font-size: 14px;
  font-weight: 600;
  font-family: monospace;
`

const LogControls = styled.div`
  display: flex;
  gap: 8px;
`

const LogButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`

const LogContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  line-height: 1.4;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`

const LogEntry = styled.div`
  margin-bottom: 4px;
  color: ${props => {
    if (props.$type === 'error') return '#ff6b6b';
    if (props.$type === 'success') return '#51cf66';
    if (props.$type === 'warning') return '#ffd43b';
    if (props.$type === 'info') return '#4ECDC4';
    return '#aaa';
  }};

  ${props => props.$highlight && `
    background: rgba(78, 205, 196, 0.1);
    padding: 2px 4px;
    border-left: 2px solid #4ECDC4;
    margin-left: -6px;
    padding-left: 4px;
  `}
`

const PROFILE_IMAGES = [
  '/playerprofiles/backpack.jpg', '/playerprofiles/bag.jpg', '/playerprofiles/broom.jpg',
  '/playerprofiles/calculator.jpg', '/playerprofiles/colorpen.png', '/playerprofiles/compass.jpg',
  '/playerprofiles/compasses.jpg', '/playerprofiles/dyestuff.jpg', '/playerprofiles/dyestuff2.jpg',
  '/playerprofiles/earth.jpg', '/playerprofiles/eraser.jpg', '/playerprofiles/glue.jpg',
  '/playerprofiles/minipan.jpg', '/playerprofiles/nitendo.jpg', '/playerprofiles/notebook.jpg',
  '/playerprofiles/pencil.jpg', '/playerprofiles/pencilcase.jpg', '/playerprofiles/photocard.jpg',
  '/playerprofiles/postit.jpg', '/playerprofiles/pouch.jpg', '/playerprofiles/protractor.jpg',
  '/playerprofiles/recorder.jpg', '/playerprofiles/ruler.jpg', '/playerprofiles/scissor.jpg',
  '/playerprofiles/setsquare.jpg', '/playerprofiles/tape.jpg'
]

export default function WaitingRoomPage() {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()

  const [roomCode, setRoomCode] = useState('')
  const [sessionInfo, setSessionInfo] = useState(null) // 세션 정보 저장 (setId, hostUserId 등)
  const [players, setPlayers] = useState([])
  const [gameSettings, setGameSettings] = useState({}) // carries mode/endCondition/problemSet/fixedQrs from router
  const [showRanking, setShowRanking] = useState(false)
  const [showFinalRanking, setShowFinalRanking] = useState(false)
  const [showQuizResults, setShowQuizResults] = useState(false)
  const [joinQrUrl, setJoinQrUrl] = useState(null)
  const [objectQrCodes, setObjectQrCodes] = useState([])
  const [isQrListExpanded, setIsQrListExpanded] = useState(false)
  const socketRef = useRef(null)
  const roomCodeRef = useRef('') // ★★★ cleanup에서 최신 roomCode 사용하기 위한 ref ★★★
  const [logs, setLogs] = useState([])
  const [showLogs, setShowLogs] = useState(true)
  const logContentRef = useRef(null)
  const [isCopied, setIsCopied] = useState(false) // 복사 버튼 상태
  const [isGameStarting, setIsGameStarting] = useState(false) // 게임 시작 중 상태 (중복 클릭 방지)
  const [gameTimer, setGameTimer] = useState(null) // 서버에서 받은 남은 시간 (초 단위)
  const timerIntervalRef = useRef(null) // 타이머 interval 참조
  const [readyPlayers, setReadyPlayers] = useState(new Set()) // Unity 플레이어 준비 상태 추적

  const generateRoomCode = () => Array.from({ length: 6 }, () => Math.floor(Math.random() * 10)).join('')
  const getRandomProfileImage = () => PROFILE_IMAGES[Math.floor(Math.random() * PROFILE_IMAGES.length)]

  // 로그 추가 함수
  const addLog = (message, type = 'info', highlight = false) => {
    const timestamp = new Date().toLocaleTimeString('ko-KR', { hour12: false })
    setLogs(prev => {
      const newLogs = [...prev, { timestamp, message, type, highlight }]
      // 최대 200개까지만 유지 (오래된 것부터 자름)
      return newLogs.length > 200 ? newLogs.slice(-200) : newLogs
    })
    // 자동 스크롤
    setTimeout(() => {
      if (logContentRef.current) {
        logContentRef.current.scrollTop = logContentRef.current.scrollHeight
      }
    }, 10)
  }

  const clearLogs = () => setLogs([])

  // ★★★ roomCode가 변경될 때마다 ref 업데이트 ★★★
  useEffect(() => {
    roomCodeRef.current = roomCode;
  }, [roomCode]);

  // ★★★ 세션 정보 확인 - 없으면 홈으로 리디렉션 ★★★
  useEffect(() => {
    const hasSessionInfo = location.state?.room || location.state?.setId || location.state?.problemSet;

    if (!hasSessionInfo) {
      console.warn('⚠️ 세션 정보 없음 - 홈으로 리디렉션');
      alert('세션 정보가 없습니다. 홈 화면으로 돌아갑니다.');
      navigate('/', { replace: true });
    }
  }, [location.state, navigate]);

  // ★★★ 새로고침/닫기 시 세션 삭제 ★★★
  useEffect(() => {
    const handleBeforeUnload = () => {
      const currentRoomCode = roomCodeRef.current;
      if (currentRoomCode) {
        console.log(`🗑️ beforeunload - 세션 ${currentRoomCode} 삭제 시도`);

        // fetch with keepalive (페이지 닫힐 때도 전송 보장)
        const deleteUrl = `/api/sessions/${encodeURIComponent(currentRoomCode)}`;
        fetch(deleteUrl, {
          method: 'DELETE',
          keepalive: true // 페이지 닫혀도 요청 유지
        }).catch(err => console.error('세션 삭제 실패:', err));

        console.log(`✅ 세션 삭제 요청 전송: ${currentRoomCode}`);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // dependency 없음 - ref는 항상 최신 값 참조

  // init room + add host + socket connect (host)
  useEffect(() => {
    const maybeRoom = location.state?.room
    setGameSettings(location.state || {})

    // 초기 플레이어 목록 비우기 (Host는 표시하지 않음, Unity 플레이어만 표시)
    setPlayers([])

    // Connect socket when roomCode is available
    const connectSocket = (code) => {
      if (!code) {
        console.log('❌ connectSocket called without roomCode')
        return
      }
      const socketUrl = maybeRoom?.socketUrl || `${window.location.origin}/sessions`
      console.log('🔌 Attempting socket connection:', { socketUrl, roomCode: code })

      const s = io(socketUrl, {
        transports: ['polling', 'websocket'],
        query: { roomCode: code, role: 'host' },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        timeout: 120000,  // 120초 연결 타임아웃 (서버와 동일)
        pingTimeout: 120000,  // 120초 ping 타임아웃 (서버와 동일)
        pingInterval: 15000  // 15초 ping 주기 (서버와 동일)
      })
      socketRef.current = s

      s.on('connect', () => {
        console.log('========================================')
        console.log('✅ HOST SOCKET CONNECTED')
        console.log('========================================')
        console.log('Socket ID:', s?.id ?? 'undefined')
        console.log('Connected:', s?.connected ?? false)
        console.log('Transport:', s?.io?.engine?.transport?.name ?? 'unknown')
        console.log('Room Code:', code)
        console.log('========================================')

        addLog(`✅ 소켓 연결 성공`, 'success', true)
        addLog(`Socket ID: ${s?.id}`, 'info')
        addLog(`Room: ${code}`, 'info')
        addLog(`Transport: ${s?.io?.engine?.transport?.name ?? 'unknown'}`, 'info')
      })

      s.on('connect_error', (err) => {
        console.error('========================================')
        console.error('❌ SOCKET CONNECTION ERROR')
        console.error('========================================')
        console.error('Error message:', err?.message ?? 'unknown')
        console.error('Error type:', err?.type ?? 'unknown')
        console.error('========================================')

        addLog(`❌ 연결 오류: ${err?.message ?? 'unknown'}`, 'error', true)
      })

      s.on('disconnect', (reason) => {
        console.log('🔌 Socket disconnected')
        console.log('  - Reason:', reason ?? 'unknown')

        addLog(`🔌 연결 끊김: ${reason ?? 'unknown'}`, 'warning')
      })

      // ✅ session:data 이벤트 - 세션 데이터(점수 포함) 수신
      s.on('session:data', (sessionData) => {
        console.log('📦 session:data received:', sessionData)

        // ★★★ sessionInfo 업데이트 (게임 모드 정보 포함) ★★★
        if (sessionData) {
          console.log('✅ sessionInfo 업데이트:', {
            endCondition: sessionData.endCondition,
            timeLimitMinutes: sessionData.timeLimitMinutes,
            scoreTarget: sessionData.scoreTarget,
            roomType: sessionData.roomType,
            progressMode: sessionData.progressMode
          })
          setSessionInfo(prev => ({
            ...prev,
            ...sessionData
          }))
        }

        // 세션에 점수 정보가 있으면 플레이어 점수 복원
        if (sessionData?.scores) {
          console.log('💾 세션에서 점수 복원:', sessionData.scores)
          setPlayers(prev => {
            return prev.map(player => {
              const savedScore = sessionData.scores[player.id] ?? sessionData.scores[player.sessionId]
              if (savedScore !== undefined) {
                console.log(`  ✅ ${player.name} 점수 복원: ${savedScore}점`)
                return { ...player, score: savedScore }
              }
              return player
            })
          })
          addLog(`💾 세션에서 점수 복원 완료`, 'success')
        }
      })

      s.on('player:joined', (p) => {
        console.log('========================================')
        console.log('🎮 PLAYER:JOINED EVENT RECEIVED')
        console.log('========================================')
        console.log('Player data type:', typeof p)
        console.log('Player data:', p)
        console.log('Player id:', p?.id ?? 'undefined')
        console.log('Player name:', p?.name ?? 'undefined')
        console.log('Player role:', p?.role ?? 'undefined')
        console.log('Timestamp:', new Date().toISOString())

        if (p === null || p === undefined) {
          console.error('⚠️ Player data is null or undefined!')
          console.error('========================================')
          addLog('⚠️ 플레이어 데이터 null', 'error')
          return
        }

        if (!p.id) {
          console.error('⚠️ Player ID is missing!')
          console.error('========================================')
          addLog('⚠️ 플레이어 ID 없음', 'error')
          return
        }

        // ★ 웹 클라이언트(HOST)가 자기 자신을 플레이어로 추가하지 않도록 필터링
        // 서버에서 보내는 player:joined에 자기 자신의 소켓ID가 포함될 수 있으므로 체크
        if (p.id === s.id) {
          console.log('⚠️ 자기 자신(HOST)의 player:joined 이벤트 무시')
          console.log('========================================')
          return
        }

        setPlayers(prev => {
          console.log('Current players count:', prev?.length ?? 0)
          const exists = prev?.some(x => x?.id === p.id) ?? false
          console.log('Player exists:', exists)

          if (exists) {
            console.log('⚠️ Skipping duplicate player')
            console.log('========================================')
            addLog(`⚠️ 중복 플레이어: ${p.name}`, 'warning')
            return prev
          }

          const newPlayer = {
            id: p.id,
            name: p.name ?? p.name ?? `Player ${(prev?.length ?? 0) + 1}`,
            profileImage: p.avatarUrl ?? getRandomProfileImage(),
            isHost: false,
            ip: p.ip ?? 'Unknown',
            sessionId: p.sessionId ?? p.id,
            score: 0 // ✅ 초기 점수 0점
          }

          console.log('✅ Adding new player:', newPlayer.name)
          console.log('New player full data:', newPlayer)
          const updated = [...(prev ?? []), newPlayer]
          console.log('New players count:', updated.length)
          console.log('========================================')

          addLog(`👋 ${newPlayer.name}님이 접속하셨습니다`, 'success', true)
          addLog(`총 ${updated.length}명`, 'info')

          return updated
        })
      })

      s.on('player:left', (p) => {
        console.log('👋 player:left event received')
        console.log('  - Player id:', p?.id ?? 'undefined')

        if (!p || !p.id) {
          console.warn('⚠️ Invalid player:left data')
          return
        }

        setPlayers(prev => {
          const filtered = (prev ?? []).filter(x => x?.id !== p.id)
          console.log('  - Removed player, remaining:', filtered.length)
          addLog(`👋 플레이어 퇴장`, 'warning')
          addLog(`남은 인원: ${filtered.length}명`, 'info')
          return filtered
        })

        // 준비 상태에서도 제거
        setReadyPlayers(prev => {
          const newSet = new Set(prev)
          newSet.delete(p.id)
          return newSet
        })
      })

      // ✅ Unity 플레이어 준비 완료 이벤트 수신
      s.on('participant:ready', (data) => {
        console.log('========================================')
        console.log('✅ participant:ready event received')
        console.log('========================================')
        console.log('Socket ID:', data?.socketId ?? 'undefined')
        console.log('Name:', data?.name ?? 'undefined')
        console.log('========================================')

        if (!data || !data.socketId) {
          console.warn('⚠️ Invalid participant:ready data')
          return
        }

        setReadyPlayers(prev => {
          const newSet = new Set(prev)
          newSet.add(data.socketId)
          console.log(`✅ 준비 완료: ${data.name} (총 ${newSet.size}명 준비)`)
          addLog(`✅ ${data.name}님 준비 완료 (${newSet.size}명)`, 'success', true)
          return newSet
        })
      })

      // ✅ Unity 플레이어 준비 취소 이벤트 수신
      console.log('🔧 [WaitingRoom] participant:unready 이벤트 리스너 등록 시작')
      console.log('  Socket connected:', s?.connected ?? false)
      console.log('  Socket ID:', s?.id ?? 'undefined')
      s.on('participant:unready', (data) => {
        console.log('========================================')
        console.log('⏹️ participant:unready event received')
        console.log('========================================')
        console.log('Socket ID:', data?.socketId ?? 'undefined')
        console.log('Name:', data?.name ?? 'undefined')
        console.log('현재 시각:', new Date().toLocaleTimeString())
        console.log('========================================')

        if (!data || !data.socketId) {
          console.warn('⚠️ Invalid participant:unready data')
          return
        }

        setReadyPlayers(prev => {
          const newSet = new Set(prev)
          newSet.delete(data.socketId)
          console.log(`⏹️ 준비 취소: ${data.name} (총 ${newSet.size}명 준비)`)
          addLog(`⏹️ ${data.name}님 준비 취소 (${newSet.size}명)`, 'warning', true)
          return newSet
        })
      })
      console.log('✅ [WaitingRoom] participant:unready 이벤트 리스너 등록 완료')

      // ✅ 아이템 사용 이벤트 수신
      s.on('item:used', (data) => {
        console.log('========================================')
        console.log('🎁 item:used event received:', data)
        console.log('========================================')

        if (!data || !data.item) {
          console.warn('⚠️ Invalid item:used data')
          addLog('⚠️ 아이템 데이터 오류', 'error')
          return
        }

        const playerName = data.playerName || '알 수 없음'
        const itemName = data.item.name || '아이템'
        const itemId = data.item.id || 'unknown'
        const itemDescription = data.item.description || ''
        const effectResult = data.effectResult

        // 기본 아이템 사용 로그 (상세)
        addLog(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'info')
        addLog(`🎯 ${playerName}님이 ${itemName}을(를) 사용하셨습니다`, 'success', true)
        addLog(`📦 아이템명: "${itemName}" (${itemId})`, 'info')
        if (itemDescription) {
          addLog(`📝 설명: ${itemDescription}`, 'info')
        }

        // 아이템 효과 상세 로그
        if (effectResult) {
          const effectType = effectResult.type
          const effectValue = effectResult.value
          const targetPlayer = effectResult.targetPlayerName
          const oldScore = effectResult.oldScore || 0
          const newScore = effectResult.newScore || 0
          const scoreDiff = newScore - oldScore

          addLog(`🎯 효과 타입: ${effectType}`, 'info')

          switch (effectType) {
            case 'score_bonus':
              addLog(`🎰 로또 (고정 점수 보너스)`, 'success')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  추가 점수: +${effectValue}점`, 'success')
              addLog(`  점수 변화: ${oldScore}점 → ${newScore}점 (${scoreDiff > 0 ? '+' : ''}${scoreDiff}점)`, 'success')
              break
            case 'score_multiplier':
              // v2: 주사위 면(1~6) × 베이스 500점 = +500~+3000 가변 보너스 (현재 점수 곱셈 아님)
              addLog(`🎲 주사위 (랜덤 보너스)`, 'success')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  굴림: ${effectValue}면 → +${scoreDiff}점`, 'success')
              addLog(`  점수 변화: ${oldScore}점 → ${newScore}점`, 'success')
              break
            case 'currency':
              // 금화 — 고정 +N점 (현재 200)
              addLog(`💰 금화 (점수 보너스)`, 'success')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  추가 점수: +${effectValue}점`, 'success')
              addLog(`  점수 변화: ${oldScore}점 → ${newScore}점 (${scoreDiff > 0 ? '+' : ''}${scoreDiff}점)`, 'success')
              break
            case 'score_swap':
              // ★ targetPlayerName 은 '아이템 사용자' (서버 명명 혼동). 실제 교환 상대는 swapWithPlayerName.
              const swapPartner = effectResult.swapWithPlayerName || '알 수 없음'
              addLog(`🃏 카드놀이 (점수 교환)`, 'warning')
              addLog(`  교환 대상: ${swapPartner}님`, 'warning')
              addLog(`  ${playerName}님과 ${swapPartner}님의 점수가 서로 바뀜`, 'warning')
              if (typeof effectResult.playerNewScore === 'number' && typeof effectResult.targetNewScore === 'number') {
                addLog(`  ${playerName}: → ${effectResult.playerNewScore}점`, 'info')
                addLog(`  ${swapPartner}: → ${effectResult.targetNewScore}점`, 'info')
              }
              break
            case 'time_extension':
              addLog(`🍞 빵 (시간 연장)`, 'success')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  다음 문제 시간 +${effectValue}초`, 'success')
              break
            case 'unlimited_time':
              addLog(`⏳ 모래시계 (시간 무제한)`, 'success')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  다음 ${effectValue}문제 동안 시간 제한 없음`, 'success')
              break
            case 'reduce_time':
              // v2: 상대 모두 -N초 + 사용자 본인 +N초
              addLog(`🕳️ 블랙홀 (시공간 비틀기)`, 'warning')
              addLog(`  사용자: ${playerName}님 → 본인 다음 문제 +${effectValue}초`, 'success')
              if (effectResult.targetPlayerIds && effectResult.targetPlayerIds.length > 0) {
                addLog(`  공격 대상: ${effectResult.targetPlayerIds.length}명 → 각자 다음 문제 -${effectValue}초`, 'error')
              }
              break
            case 'reveal_answer':
              addLog(`📝 답안지 (정답 공개)`, 'info')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  다음 ${effectValue}문제 정답을 미리 알려줍니다`, 'success')
              break
            case 'view_answers':
              addLog(`📄 컨닝 페이퍼 (정답 슬쩍 보기)`, 'info')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  다음 ${effectValue}문제 정답이 어렴풋이 보입니다`, 'success')
              break
            case 'auto_solve':
              addLog(`🪔 요술램프 (자동 정답)`, 'success')
              addLog(`  대상: ${targetPlayer || playerName}님`, 'info')
              addLog(`  다음 문제를 자동으로 정답 처리합니다`, 'success')
              break
            case 'item_reward':
              // 서버는 카드놀이 솔로(상대 없음) 시 'item_reward' 로 타입 위장해서 보냄. 발동 아이템으로 분기.
              const rewardItem = effectResult.item
              const isSoloCard = data.item?.id === 'epic_card_game'
              if (isSoloCard) {
                addLog(`🃏 카드놀이 (솔로 보상)`, 'info')
                addLog(`  교환할 상대가 없어 랜덤 아이템으로 대체`, 'info')
              } else {
                addLog(`📦 보물상자 (랜덤 아이템 획득)`, 'success')
              }
              if (rewardItem) {
                addLog(`  획득: ${rewardItem.name} (${rewardItem.rarity})`, 'success')
              }
              break
            case 'team_bonus':
              addLog(`🎒 책가방 (팀 보너스)`, 'success')
              addLog(`  사용자: ${playerName}님`, 'info')
              addLog(`  효과: 게임의 모든 플레이어에게 +${effectValue}점`, 'success')
              if (effectResult.newScores) {
                const recipients = Object.entries(effectResult.newScores)
                addLog(`  적용 인원: ${recipients.length}명`, 'info')
                recipients.forEach(([pid, score]) => {
                  addLog(`    - ${pid}: 현재 ${score}점`, 'info')
                })
              }
              break
            default:
              addLog(`✨ 미정의 효과 타입: ${effectType}`, 'warning')
              if (effectValue) {
                addLog(`  효과값: ${effectValue}`, 'info')
              }
              if (targetPlayer) {
                addLog(`  대상: ${targetPlayer}님`, 'info')
              }
          }

          // ✅ 기본 점수 표시 (모든 아이템에 적용)
          if (effectResult.baseScoreBonus) {
            addLog(`💰 기본 점수: +${effectResult.baseScoreBonus}점`, 'success')
            addLog(`  총 점수 변화: ${effectResult.oldScore || 0}점 → ${effectResult.newScore || 0}점`, 'success')
          }
        } else {
          addLog(`⚠️ 효과 정보 없음`, 'warning')
        }

        addLog(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, 'info')

        console.log(`  📋 아이템 ID: ${itemId}`)
        console.log(`  👤 사용자: ${playerName}`)
        console.log(`  🎯 효과:`, effectResult)
      })

      // ✅ 점수 업데이트 이벤트 수신 (웹 클라이언트용)
      s.on('score:update', (data) => {
        console.log('========================================')
        console.log('📊 score:update event received:', data)
        console.log('Scores:', data?.scores ?? {})
        console.log('Trigger:', data?.trigger ?? {})
        console.log('Timestamp:', data?.timestamp ?? 'unknown')
        console.log('========================================')

        if (!data || !data.scores) {
          console.warn('⚠️ Invalid score:update data')
          addLog('⚠️ 점수 업데이트 데이터 없음', 'warning')
          return
        }

        const playerName = data.trigger?.playerName || '알 수 없음'
        const itemName = data.trigger?.itemName || '아이템'
        const scoreCount = Object.keys(data.scores).length

        addLog(`📊 점수 업데이트: ${playerName}님 "${itemName}" 사용`, 'info', true)
        addLog(`📊 총 ${scoreCount}명 점수 업데이트됨`, 'success')

        // 각 플레이어 점수 로그
        Object.entries(data.scores).forEach(([playerId, score]) => {
          console.log(`  - Player ${playerId}: ${score}점`)
        })

        // ✅ 플레이어 점수 업데이트 (실시간 점수 표 반영)
        setPlayers(prev => {
          console.log('🔍 Updating players. Current count:', prev.length)
          console.log('🔍 Current players:', prev.map(p => ({ name: p.name, id: p.id, sessionId: p.sessionId, score: p.score })))

          return prev.map(player => {
            // data.scores 객체에서 해당 플레이어의 점수 찾기
            const newScore = data.scores[player.id] ?? data.scores[player.sessionId]
            const oldScore = player.score ?? 0
            const scoreDiff = newScore !== undefined ? newScore - oldScore : 0

            console.log(`  🔍 Player: ${player.name}, ID: ${player.id}, SessionID: ${player.sessionId}, NewScore: ${newScore}`)

            if (newScore !== undefined) {
              console.log(`  ✅ ${player.name} 점수 업데이트: ${oldScore}점 → ${newScore}점 (${scoreDiff > 0 ? '+' : ''}${scoreDiff}점)`)
              return { ...player, score: newScore }
            }

            console.log(`  ⚠️ ${player.name} 점수 업데이트 실패 - scores 객체에 키 없음`)
            console.log(`  📋 현재 scores 객체 키 목록: ${Object.keys(data.scores).join(', ')}`)
            return player
          })
        })

        addLog(`✅ 점수 표 업데이트 완료`, 'success')
      })

      // ✅ 게임 클리어 이벤트 수신 (모든 퀴즈 완료)
      s.on('game:clear', (data) => {
        console.log('========================================')
        console.log('🎉 game:clear event received:', data)
        console.log('========================================')
        console.log('Room:', data?.roomCode ?? 'unknown')
        console.log('Completed by:', data?.completedBy ?? 'unknown')
        console.log('Timestamp:', data?.timestamp ?? 'unknown')
        console.log('========================================')

        addLog(`🎉🎉🎉 게임 클리어! 대탈출 성공!`, 'success', true)
        addLog(`완료: ${data?.completedBy ?? '알 수 없음'}`, 'info')

        // ★★★ 승자 결정 (점수가 가장 높은 플레이어) ★★★
        let winnerName = ''

        setPlayers(currentPlayers => {
          if (currentPlayers.length > 0) {
            // 점수 순으로 정렬
            const sortedPlayers = [...currentPlayers].sort((a, b) => (b.score || 0) - (a.score || 0))
            const winner = sortedPlayers[0]

            console.log('🏆 승자 결정:')
            console.log(`  - 이름: ${winner.name}`)
            console.log(`  - 점수: ${winner.score || 0}점`)

            addLog(`🏆 승자: ${winner.name} (${winner.score || 0}점)`, 'success', true)
            winnerName = `${winner.name} (${winner.score || 0}점)`

            // ★★★ Unity 클라이언트에게 승자 정보 브로드캐스트 ★★★
            if (socketRef.current) {
              const winnerData = {
                roomCode: roomCode,
                winner: {
                  name: winner.name,
                  score: winner.score || 0,
                  id: winner.id
                },
                rankings: sortedPlayers.map((p, index) => ({
                  rank: index + 1,
                  name: p.name,
                  score: p.score || 0,
                  id: p.id
                })),
                timestamp: new Date().toISOString()
              }

              socketRef.current.emit('game:winner', winnerData)
              console.log('📤 game:winner 이벤트 전송:', winnerData)
              addLog(`📤 승자 정보 Unity로 전송 완료`, 'success')
            }
          }
          return currentPlayers
        })

        // 게임 시작 버튼 다시 활성화 (재시작 가능하도록)
        setIsGameStarting(false)
        addLog(`✅ 게임 시작 버튼 다시 활성화`, 'success')

        // 준비 상태 초기화 (재시작 대비)
        setReadyPlayers(new Set())
        addLog(`🔄 준비 상태 초기화`, 'info')

        // 알림 표시 (승자 정보 포함)
        setTimeout(() => {
          const message = winnerName
            ? `🎉 모든 퀴즈를 완료했습니다! 대탈출 성공!\n\n🏆 승자: ${winnerName}\n\n다시 게임을 시작하시겠습니까?`
            : '🎉 모든 퀴즈를 완료했습니다! 대탈출 성공!\n\n다시 게임을 시작하시겠습니까?'

          if (window.confirm(message)) {
            // 게임 시작 버튼은 이미 활성화되어 있으므로 사용자가 직접 클릭 가능
            addLog(`사용자가 재시작 선택`, 'info')
          }
        }, 1000)
      })

      // ✅ 게임 리셋 이벤트 수신 (모든 유니티 클라이언트가 나감)
      s.on('game:reset', (data) => {
        console.log('========================================')
        console.log('🔄 game:reset event received:', data)
        console.log('========================================')
        console.log('Room:', data?.roomCode ?? 'unknown')
        console.log('Message:', data?.message ?? 'unknown')
        console.log('Timestamp:', data?.timestamp ?? 'unknown')
        console.log('========================================')

        addLog(`🔄 모든 플레이어가 나갔습니다`, 'info', true)
        addLog(`${data?.message ?? '게임을 다시 시작할 수 있습니다.'}`, 'info')

        // 게임 시작 버튼 다시 활성화
        setIsGameStarting(false)

        // 준비 상태 초기화
        setReadyPlayers(new Set())
        addLog(`🔄 준비 상태 초기화`, 'info')

        // 현재 플레이어 수 확인
        setPlayers(prev => {
          console.log(`📊 현재 플레이어 수: ${prev.length}`)
          addLog(`📊 현재 플레이어 수: ${prev.length}명`, 'info')

          if (prev.length >= 2) {
            addLog(`✅ 게임 시작 버튼 활성화됨 (플레이어 ${prev.length}명)`, 'success')
          } else {
            addLog(`⏳ 새로운 플레이어 대기 중 (현재 ${prev.length}명, 최소 2명 필요)`, 'warning')
          }

          return prev
        })
      })

      // ✅ Claude AI 페르소나 채팅 요청 받기 (회원 API 키 사용)
      s.on('persona:chat:request', async (data) => {
        console.log('🤖 persona:chat:request received:', data)
        addLog(`🤖 AI 요청: ${data?.arObjectId}`, 'info')

        try {
          // 서버 API 호출 (회원의 API 키를 자동으로 조회하여 사용)
          const response = await fetch('/auth/claude/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include', // 세션 쿠키 포함
            body: JSON.stringify({
              roomId: sessionInfo?.roomCode,
              setId: sessionInfo?.setId,
              hostUserId: sessionInfo?.hostUserId,
              arObjectId: data.arObjectId,
              userMessage: data.userMessage,
              conversationHistory: data.conversationHistory || []
            })
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || `서버 오류: ${response.status}`)
          }

          const result = await response.json()

          if (!result.success) {
            throw new Error(result.error || 'AI 응답 생성 실패')
          }

          // Unity 클라이언트에게 AI 응답 전송
          s.emit('persona:chat:response', {
            requestId: data.requestId,
            playerId: data.playerId,
            ok: true,
            objectName: result.data.objectName,
            response: result.data.response,
            usage: result.data.usage
          })

          addLog(`✅ AI 응답 전송: ${result.data.objectName}`, 'success')

        } catch (error) {
          console.error('❌ Persona Chat 오류:', error)
          addLog(`❌ AI 오류: ${error.message}`, 'error')

          s.emit('persona:chat:response', {
            requestId: data.requestId,
            playerId: data.playerId,
            ok: false,
            error: error.message
          })
        }
      })
    }

    // Create backend session and use its roomCode/QR
    ;(async () => {
      try {
        const ls = location.state || {}
        const setId = (ls.problemSet && (ls.problemSet.id || ls.problemSet.set_id)) || ls.problemSetId
        console.log('🔍 WaitingRoom init:', { locationState: ls, setId })

        // ★ GameReadyPage 에서 이미 세션을 만들고 인쇄 QR을 발급한 경우 → 그대로 재사용.
        //   (재생성하면 roomCode 가 바뀌어 인쇄된 QR이 무효화됨)
        if (ls.skipSessionCreate && maybeRoom?.roomCode) {
          try {
            const checkRes = await fetch(
              `/api/sessions/${encodeURIComponent(maybeRoom.roomCode)}`,
              { credentials: 'include' }
            )
            if (checkRes.ok) {
              const checkData = await checkRes.json()
              if (checkData?.success && checkData.session?.roomCode) {
                console.log('♻️ GameReady 에서 미리 만든 세션 재사용:', checkData.session.roomCode)
                setRoomCode(checkData.session.roomCode)
                setSessionInfo({
                  ...checkData.session,
                  mode: ls.mode || checkData.session.mode,
                })
                connectSocket(checkData.session.roomCode)
                return
              }
            }
            console.warn('⚠️ 미리 만든 세션을 찾지 못함 → 새로 생성합니다.')
          } catch (err) {
            console.warn('미리 만든 세션 확인 오류 → 새로 생성:', err)
          }
        }

        if (setId) {
          // Create from quiz set: include objects/problems from DB
          console.log('📡 Fetching session from quiz set:', setId)

          // ★ 커버 이미지 포함하여 요청 (DB 컬럼명: set_cover_img)
          const coverImageUrl = ls.problemSet?.set_cover_img || ls.problemSet?.image || ls.problemSet?.cover_image || null
          console.log('🖼️ 커버 이미지 URL:', coverImageUrl)

          // ★★★ 게임 모드 옵션 준비 (location.state에서 전달받음)
          const gameOptions = {
            coverImage: coverImageUrl,
            roomType: ls.roomType || undefined,  // 'test' | 'game'
            progressMode: ls.progressMode || undefined,  // 'sequential' | 'qr_scan'
            endCondition: ls.endCondition || undefined,  // 'none' | 'time_limit' | 'score_target'
            timeLimitMinutes: ls.timeLimitMinutes || undefined,
            scoreTarget: ls.scoreTarget || undefined,
            hostUserId: ls.hostUserId || undefined  // 작성자 확인용
          };
          console.log('🎮 게임 옵션:', gameOptions);

          const res = await fetch(`/api/sessions/from-quiz/${setId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(gameOptions)
          })

          const data = await res.json()
          console.log('📥 Session API response:', { ok: res.ok, data })
          if (res.ok && data?.success && data.session?.roomCode) {
            console.log('✅ Setting roomCode and connecting socket:', data.session.roomCode)
            setRoomCode(data.session.roomCode)

            // ★★★ location.state의 게임 설정을 sessionInfo에 merge ★★★
            // mode를 기반으로 올바른 형식으로 변환
            const mode = ls.mode;
            const rawEndCondition = ls.endCondition; // '100000' or '15' 같은 숫자 문자열

            let parsedEndCondition, parsedTimeLimitMinutes, parsedScoreTarget;

            if (mode === 'time') {
              parsedEndCondition = 'time_limit';
              parsedTimeLimitMinutes = parseInt(rawEndCondition) || ls.timeLimitMinutes;
              parsedScoreTarget = undefined;
            } else if (mode === 'money') {
              parsedEndCondition = 'score_target';
              parsedTimeLimitMinutes = undefined;
              parsedScoreTarget = parseInt(rawEndCondition) || ls.scoreTarget;
            } else {
              // 기존 형식 사용 (API 응답)
              parsedEndCondition = data.session.endCondition;
              parsedTimeLimitMinutes = data.session.timeLimitMinutes;
              parsedScoreTarget = data.session.scoreTarget;
            }

            const mergedSessionInfo = {
              ...data.session,
              mode: mode || data.session.mode,
              endCondition: parsedEndCondition,
              timeLimitMinutes: parsedTimeLimitMinutes,
              scoreTarget: parsedScoreTarget,
              roomType: ls.roomType || data.session.roomType,
              progressMode: ls.progressMode || data.session.progressMode
            };

            console.log('🎮 Merged sessionInfo:', {
              mode: mergedSessionInfo.mode,
              endCondition: mergedSessionInfo.endCondition,
              timeLimitMinutes: mergedSessionInfo.timeLimitMinutes,
              scoreTarget: mergedSessionInfo.scoreTarget
            });

            setSessionInfo(mergedSessionInfo) // 세션 정보 저장 (게임 설정 포함)
            connectSocket(data.session.roomCode)
            return
          }
        } else {
          // Fallback: create an empty session but try to include any state-provided data
          console.log('📡 Creating empty session (no setId)')
          const candidatesObjects = ls.selectedObjects || ls.quizObjects || ls.objects || []
          const mappedObjects = Array.isArray(candidatesObjects)
            ? candidatesObjects.map(o => ({
                name: o.name || o.object_name || o.title || 'object',
                prefabKey: o.prefabKey || o.prefab_key || null,
                thumbnail: o.image_url || o.thumbnail || null,
              }))
            : []
          const rawProblems =
            ls.problems ||
            ls.questions ||
            (ls.problemSet && (ls.problemSet.problems || ls.problemSet.questions || ls.problemSet.items)) ||
            []
          const mappedProblems = Array.isArray(rawProblems)
            ? rawProblems.map(p => {
                const choices = p.answers || p.choices || p.options || []
                const correctIndices = p.correct_answer || p.correctAnswer || []
                // ★ correct_answer가 인덱스 배열이면 첫 번째 정답의 텍스트로 변환
                let answerText = p.answer // 이미 텍스트인 경우 그대로 사용
                if (!answerText && Array.isArray(correctIndices) && correctIndices.length > 0 && choices.length > 0) {
                  const firstCorrectIndex = correctIndices[0]
                  if (firstCorrectIndex >= 0 && firstCorrectIndex < choices.length) {
                    answerText = choices[firstCorrectIndex]
                  }
                }
                return {
                  questionId: p.questionId || p.id || p.qid || null,
                  title: p.title || p.question || '',
                  objectName: p.objectName || p.object || p.object_name || null,
                  answers: choices,
                  answer: answerText,
                }
              })
            : []
          const sessionPayload = {}
          if (mappedObjects.length) sessionPayload.objects = mappedObjects
          if (mappedProblems.length) sessionPayload.problems = mappedProblems
          const res = await fetch('/api/sessions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(sessionPayload)
          })
          const data = await res.json()
          console.log('📥 Empty session API response:', { ok: res.ok, data })
          if (res.ok && data?.success && data.session?.roomCode) {
            console.log('✅ Setting roomCode and connecting socket:', data.session.roomCode)
            setRoomCode(data.session.roomCode)
            setSessionInfo(data.session) // 세션 정보 저장 (setId, hostUserId 포함)
            connectSocket(data.session.roomCode)
            return
          }
        }

        // Last resort: use roomCode passed in
        console.log('⚠️ No session created, checking maybeRoom:', maybeRoom)
        if (maybeRoom?.roomCode) {
          console.log('✅ Using roomCode from location.state:', maybeRoom.roomCode)
          setRoomCode(maybeRoom.roomCode)
          connectSocket(maybeRoom.roomCode)
        }
      } catch (err) {
        console.error('❌ Error creating session:', err)
        if (maybeRoom?.roomCode) {
          console.log('✅ Fallback: Using roomCode from location.state:', maybeRoom.roomCode)
          setRoomCode(maybeRoom.roomCode)
          connectSocket(maybeRoom.roomCode)
        }
      }
    })()

    return () => {
      // ★★★ 컴포넌트 언마운트 시 (다른 페이지로 이동 시) 세션 삭제 ★★★
      console.log('🔄 WaitingRoomPage cleanup - 세션 삭제 시작');

      // 소켓 연결 해제
      if (socketRef.current) {
        socketRef.current.disconnect();
        console.log('✅ 소켓 연결 해제 완료');
      }

      // 세션 삭제 (방 코드가 있을 때만) - ref 사용하여 최신 값 참조
      const currentRoomCode = roomCodeRef.current;
      if (currentRoomCode) {
        console.log(`🗑️ 세션 ${currentRoomCode} 삭제 요청`);
        fetch(`/api/sessions/${encodeURIComponent(currentRoomCode)}`, {
          method: 'DELETE',
          keepalive: true
        }).then(() => {
          console.log(`✅ 세션 ${currentRoomCode} 삭제 완료`);
        }).catch(err => {
          console.error('세션 삭제 실패:', err);
        });
      }
    }
  }, [location.state])

  // build session QR (server-generated image)
  useEffect(() => {
    const maybeRoom = location.state?.room
    if (maybeRoom?.joinQrUrl) {
      setJoinQrUrl(maybeRoom.joinQrUrl)
      return
    }
    if (roomCode) {
      setJoinQrUrl(`/api/sessions/${encodeURIComponent(roomCode)}/qr`)
    } else {
      setJoinQrUrl(null)
    }
  }, [location.state, roomCode])

  useEffect(() => {
    const mapCandidates = (list = []) => {
      const store = new Map()
      const push = (candidate = {}) => {
        const name =
          candidate.name ||
          candidate.objectName ||
          candidate.object_name ||
          candidate.arObject ||
          candidate.arObjectName ||
          candidate.ar_object_name
        if (!name || typeof name !== 'string') return
        const rawId =
          candidate.id ||
          candidate.objectId ||
          candidate.object_id ||
          candidate.ar_object_id ||
          candidate.objectID ||
          candidate.arObjectId
        const prefab =
          candidate.prefabKey ||
          candidate.prefab_key ||
          candidate.prefab ||
          null
        const thumbnail =
          candidate.thumbnail ||
          candidate.image_url ||
          candidate.imageUrl ||
          candidate.ar_object_image ||
          candidate.arObjectImage ||
          null
        const key = `${name.trim()}::${rawId ? String(rawId).trim() : ''}`
        if (!store.has(key)) {
          store.set(key, {
            name: name.trim(),
            id: rawId ? String(rawId).trim() : null,
            prefabKey: prefab || null,
            thumbnail: thumbnail || null
          })
        }
      }
      list.forEach(push)
      return Array.from(store.values()).sort((a, b) => a.name.localeCompare(b.name, 'ko'))
    }

    const gatherFromState = () => {
      const ps = gameSettings?.problemSet
      const bucket = []
      if (Array.isArray(ps?.objects)) bucket.push(...ps.objects)
      if (Array.isArray(ps?.problems)) bucket.push(...ps.problems)
      if (Array.isArray(gameSettings?.selectedObjects)) bucket.push(...gameSettings.selectedObjects)
      if (Array.isArray(gameSettings?.objects)) bucket.push(...gameSettings.objects)
      if (Array.isArray(gameSettings?.fixedQrs)) {
        bucket.push(
          ...gameSettings.fixedQrs.map((item) => ({
            name: item?.objectName || item?.arObjectName,
            objectId: item?.objectId || item?.arObjectId
          }))
        )
      }
      return mapCandidates(bucket)
    }

    let cancelled = false
    ;(async () => {
      try {
        let candidates = gatherFromState()

        if (!candidates.length && roomCode) {
          try {
            const res = await fetch(`/api/sessions/${encodeURIComponent(roomCode)}`, {
              credentials: 'include'
            })
            if (res.ok) {
              const data = await res.json()
              const session = data?.session || {}
              const remoteBucket = []
              if (Array.isArray(session.objects)) remoteBucket.push(...session.objects)
              if (Array.isArray(session.problems)) remoteBucket.push(...session.problems)
              candidates = mapCandidates(remoteBucket)
            }
          } catch (err) {
            console.warn('Failed to fetch session for object QR:', err)
          }
        }

        if (!candidates.length) {
          if (!cancelled) setObjectQrCodes([])
          return
        }

        const generated = []
        for (let i = 0; i < candidates.length; i++) {
          const candidate = candidates[i]
          // ★★★ Unity QuizProgressManager와 호환되는 형식으로 QR 생성 ★★★
          const payload = {
            type: 'unlock-object',  // Unity에서 오브젝트 잠금 해제용 QR로 인식
            roomCode: roomCode,  // 방 번호 (필수)
            objectName: candidate.name,  // 오브젝트 이름 (필수)
            objectIndex: i  // 오브젝트 순서 (QuizProgressManager의 serverObjectOrder와 매핑)
          }
          // 추가 정보 (선택)
          if (candidate.id) payload.id = candidate.id
          if (candidate.prefabKey) payload.prefabKey = candidate.prefabKey

          const payloadJson = JSON.stringify(payload)
          const image = await QRCode.toDataURL(payloadJson, {
            width: 220,
            margin: 1,
            errorCorrectionLevel: 'M',
            color: { dark: '#0f1b2e', light: '#ffffff' }
          })
          generated.push({
            ...candidate,
            payload: payloadJson,
            image,
            objectIndex: i  // UI 표시용
          })
        }
        if (!cancelled) setObjectQrCodes(generated)
      } catch (err) {
        console.error('Object QR generation failed:', err)
        if (!cancelled) setObjectQrCodes([])
      }
    })()

    return () => {
      cancelled = true
    }
  }, [gameSettings, roomCode])

  // 더미 플레이어 생성 제거 - 실제 접속자만 표시

  // ✅ Unity 플레이어만 카운트 (Host 제외)
  const unityPlayers = players.filter(p => !p.isHost)
  const unityPlayerCount = unityPlayers.length

  // ✅ 게임 시작 조건: Unity 플레이어가 1명 이상이고, 모두 준비 완료했을 때
  const canStartGame = unityPlayerCount >= 1 && readyPlayers.size >= unityPlayerCount && !isGameStarting

  // 디버깅: canStartGame 상태 로깅
  useEffect(() => {
    console.log(`🔍 [DEBUG] canStartGame = ${canStartGame}`)
    console.log(`  - Unity 플레이어: ${unityPlayerCount}명`)
    console.log(`  - 준비 완료: ${readyPlayers.size}명`)
    console.log(`  - 게임 시작 중: ${isGameStarting}`)
  }, [canStartGame, unityPlayerCount, readyPlayers.size, isGameStarting])
  const handleStartGame = () => {
    if (!canStartGame) return
    if (isGameStarting) return // 이미 시작 중이면 무시

    // 게임 시작 중 상태로 변경 (중복 클릭 방지)
    setIsGameStarting(true)

    if (socketRef.current) {
      // 게임 시작 카운트다운 이벤트 전송 (Unity 클라이언트용)
      socketRef.current.emit('game:start', {
        roomCode,
        timestamp: new Date().toISOString()
      })

      // 세션 시작 이벤트 전송 (coverImage는 session:data에 이미 포함되어 있음)
      const sessionStartPayload = {
        roomCode,
        mode: gameSettings?.mode,
        endCondition: gameSettings?.endCondition,
        setId: gameSettings?.problemSet?.id
      };

      console.log('[WaitingRoom] session:start 전송:', sessionStartPayload);
      socketRef.current.emit('session:start', sessionStartPayload)

      // ★★★ 시간 제한 모드인 경우 서버 타이머 시작 ★★★
      console.log('[타이머 디버그] sessionInfo:', sessionInfo)
      console.log('[타이머 디버그] mode:', sessionInfo?.mode)
      console.log('[타이머 디버그] endCondition:', sessionInfo?.endCondition)
      console.log('[타이머 디버그] timeLimitMinutes:', sessionInfo?.timeLimitMinutes)

      // mode가 "time"이면 시간 제한 모드 (DB 형식)
      // 또는 endCondition이 "time_limit"이면 시간 제한 모드 (location.state 형식)
      const isTimeLimit = sessionInfo?.mode === 'time' || sessionInfo?.endCondition === 'time_limit'
      const timeLimitMinutes = sessionInfo?.timeLimitMinutes || parseInt(sessionInfo?.endCondition) || 0

      console.log('[타이머 디버그] isTimeLimit:', isTimeLimit, 'timeLimitMinutes:', timeLimitMinutes)

      if (isTimeLimit && timeLimitMinutes > 0) {
        const totalSeconds = timeLimitMinutes * 60

        console.log(`⏰ 타이머 예약: ${timeLimitMinutes}분 (카운트다운 후 시작)`)
        addLog(`⏰ 타이머 예약: ${timeLimitMinutes}분 (카운트다운 후 시작)`, 'info')

        // ★★★ 카운트다운(5초) 후에 타이머 시작 ★★★
        setTimeout(() => {
          const gameStartTime = Date.now()

          console.log(`⏰ 타이머 시작: ${timeLimitMinutes}분 (${totalSeconds}초)`)
          addLog(`⏰ 타이머 시작: ${timeLimitMinutes}분`, 'info')

          setGameTimer(totalSeconds)

          // ★★★ Unity에 게임 시작 시간 전송 (Unity가 로컬로 계산) ★★★
          socketRef.current.emit('game:timer:start', {
            roomCode: roomCode,
            gameStartTime: gameStartTime,  // 서버 시작 시간 (밀리초)
            totalSeconds: totalSeconds,     // 총 제한 시간 (초)
            timestamp: new Date().toISOString()
          })

          // 기존 타이머가 있다면 정리
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current)
          }

          // 5초마다 서버 시간 동기화 (트래픽 절감)
          timerIntervalRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - gameStartTime) / 1000)
            const remaining = Math.max(0, totalSeconds - elapsed)

            setGameTimer(remaining)

            // 5초마다 Unity에 동기화 정보 전송
            socketRef.current.emit('game:timer:sync', {
              roomCode: roomCode,
              remainingSeconds: remaining,
              serverTime: Date.now(),
              timestamp: new Date().toISOString()
            })

            console.log(`⏰ 타이머 동기화: ${remaining}초 남음`)

            // 시간 종료
            if (remaining <= 0) {
              clearInterval(timerIntervalRef.current)
              timerIntervalRef.current = null

              console.log('⏰ 시간 종료!')
              addLog('⏰ 시간 종료! 게임 종료', 'error', true)

              // 승자 결정 (점수가 가장 높은 플레이어)
              setPlayers(currentPlayers => {
                if (currentPlayers.length > 0) {
                  const sortedPlayers = [...currentPlayers].sort((a, b) => (b.score || 0) - (a.score || 0))
                  const winner = sortedPlayers[0]

                  console.log('🏆 시간 종료 - 승자 결정:')
                  console.log(`  - 이름: ${winner.name}`)
                  console.log(`  - 점수: ${winner.score || 0}점`)

                  addLog(`🏆 승자: ${winner.name} (${winner.score || 0}점)`, 'success', true)

                  // game:timeup 이벤트 전송 (시간 종료)
                  socketRef.current.emit('game:timeup', {
                    roomCode: roomCode,
                    winner: {
                      name: winner.name,
                      score: winner.score || 0,
                      id: winner.id
                    },
                    rankings: sortedPlayers.map((p, index) => ({
                      rank: index + 1,
                      name: p.name,
                      score: p.score || 0,
                      id: p.id
                    })),
                    timestamp: new Date().toISOString()
                  })

                  // 알림 표시
                  setTimeout(() => {
                    const message = `⏰ 시간 종료!\n\n🏆 승자: ${winner.name} (${winner.score || 0}점)\n\n다시 게임을 시작하시겠습니까?`
                    if (window.confirm(message)) {
                      addLog(`사용자가 재시작 선택`, 'info')
                    }
                  }, 1000)
                }
                return currentPlayers
              })

              // 게임 시작 버튼 다시 활성화
              setIsGameStarting(false)
            }
          }, 5000) // 5초마다 동기화 (트래픽 절감)
        }, 5000) // 카운트다운 5초 후 타이머 시작
      }

      // ★★★ 머니(점수) 목표 모드인 경우 목표 점수 UI 표시 ★★★
      const isScoreTarget = sessionInfo?.mode === 'money' || sessionInfo?.endCondition === 'score_target'
      const scoreTarget = sessionInfo?.scoreTarget || parseInt(sessionInfo?.endCondition) || 0

      console.log('[머니 모드 디버그] isScoreTarget:', isScoreTarget, 'scoreTarget:', scoreTarget)

      if (isScoreTarget && scoreTarget > 0) {
        console.log(`💰 머니 목표 모드: ${scoreTarget}점 (카운트다운 후 UI 표시)`)
        addLog(`💰 머니 목표: ${scoreTarget}점`, 'info')

        // ★★★ 카운트다운(5초) 후에 목표 점수 UI 표시 ★★★
        setTimeout(() => {
          console.log(`💰 머니 목표 UI 표시: ${scoreTarget}점`)

          // ★★★ Unity에 목표 점수 전송 ★★★
          socketRef.current.emit('game:score:target', {
            roomCode: roomCode,
            targetScore: scoreTarget,
            timestamp: new Date().toISOString()
          })
        }, 5000) // 카운트다운 5초 후 UI 표시
      }
    }
    // setShowRanking(true) // 페이지 변경 제거 - 대기실 유지
  }

  const getRankingData = () => {
    const colors = [
      'linear-gradient(135deg, #FFD700, #FFA500)',
      'linear-gradient(135deg, #FF6B6B, #FF8E53)',
      'linear-gradient(135deg, #4ECDC4, #44A08D)',
      'linear-gradient(135deg, #A8E6CF, #88D8A3)'
    ]
    // ✅ 실제 점수 사용 (랜덤 점수 제거)
    return players.slice(0, 4).map((p, i) => ({
      id: p.id,
      name: p.name,
      score: p.score ?? 0, // 실제 점수 (없으면 0점)
      avatar: '🙂',
      bgColor: colors[i % colors.length]
    })).sort((a, b) => b.score - a.score)
  }

  const handleGoalAchieved = () => { setShowRanking(false); setShowFinalRanking(true) }
  const handleContinueToProblemDetail = () => {
    setShowFinalRanking(false)
    navigate('/problem-detail', { state: { roomCode, players: getRankingData(), settings: gameSettings } })
  }

  // 방번호 복사 핸들러
  const handleCopyRoomCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode)
      setIsCopied(true)
      addLog(`방번호 복사됨: ${roomCode}`, 'success', true)
      // 2초 후 복사 상태 초기화
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('복사 실패:', err)
      addLog('방번호 복사 실패', 'error')
    }
  }

  const handleShowQR = () => {
    if (joinQrUrl) {
      const screenWidth = window.screen.width
      const screenHeight = window.screen.height
      const qrWindowWidth = Math.floor(screenWidth * 0.9)
      const qrWindowHeight = Math.floor(screenHeight * 0.9)
      const qrWindow = window.open('', 'QR 코드', `width=${qrWindowWidth},height=${qrWindowHeight}`)
      qrWindow.document.write(`
        <html>
          <head>
            <title>세션 접속 QR 코드</title>
            <style>
              body {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100vh;
                margin: 0;
                font-family: Arial, sans-serif;
                background-color: #f5f5f5;
              }
              h2 { color: #333; margin-bottom: 20px; font-size: 3rem; }
              img { width: 80%; height: auto; max-height: 80vh; object-fit: contain; }
            </style>
          </head>
          <body>
            <h2>방 코드: ${roomCode}</h2>
            <img src="${joinQrUrl}" alt="세션 접속 QR" />
          </body>
        </html>
      `)
      qrWindow.document.close()
    }
  }

  return (
    <Container>
      <Header>
        <img src="/logo.png" alt="logo" />
      </Header>

      <MainContent>
        {/* 왼쪽: 컨트롤 패널 */}
        <LeftSection>
          <RoomCodeDisplay>
            <RoomCodeTitle>Room Code</RoomCodeTitle>
            <RoomCodeContainer>
              <RoomCodeNumber>{roomCode}</RoomCodeNumber>
              <CopyButton
                onClick={handleCopyRoomCode}
                className={isCopied ? 'copied' : ''}
              >
                {isCopied ? '✓ 복사됨' : '📋 복사'}
              </CopyButton>
            </RoomCodeContainer>
          </RoomCodeDisplay>

          <ControlPanel>
            <GameButton $canStart={canStartGame} onClick={handleStartGame}>
              {isGameStarting
                ? '🚀 게임 시작 중...'
                : canStartGame
                ? '🎮 게임 시작'
                : unityPlayerCount === 0
                ? '⏳ 플레이어 대기 중'
                : `⏳ 준비 대기 중 (${readyPlayers.size}/${unityPlayerCount})`
              }
            </GameButton>

            <QRButton onClick={handleShowQR}>
              📱 QR 코드 보기
            </QRButton>
          </ControlPanel>
        </LeftSection>

        {/* 오른쪽: 참가자 목록 + 대시보드 */}
        <RightSection>
          {/* 상단: 참가자 목록 */}
          <PlayersContainer>
            <PlayersHeader>
              <PlayersTitle>참가자 목록</PlayersTitle>
              <PlayersCount>{unityPlayerCount}명</PlayersCount>
            </PlayersHeader>

            <WaitingText style={{ marginBottom: '25px' }}>
              {unityPlayerCount === 0
                ? '⏳ Unity 플레이어를 기다리는 중입니다...'
                : canStartGame
                ? `✅ 모든 플레이어 준비 완료! (${readyPlayers.size}/${unityPlayerCount})`
                : `⏳ 플레이어 준비 중... (${readyPlayers.size}/${unityPlayerCount})`
              }
            </WaitingText>

            <PlayersGrid>
              {unityPlayers.slice(0, 32).map((player, i) => (
                <PlayerSlot key={player.id} $hasPlayer={true} $index={i}>
                  <PlayerAvatar
                    src={player.profileImage}
                    alt={player.name}
                    onError={(e) => { e.target.src = '/playerprofiles/backpack.jpg' }}
                  />
                  <PlayerInfo>
                    <PlayerName>{player.name}</PlayerName>
                  </PlayerInfo>
                </PlayerSlot>
              ))}
            </PlayersGrid>
          </PlayersContainer>

          {/* 하단: 실시간 대시보드 */}
          <QuizResultsPanel
            roomCode={roomCode}
            isOpen={true}
            onClose={() => {}}
            socket={socketRef.current}
          />
        </RightSection>
      </MainContent>

      {objectQrCodes.length > 0 && (
        <ObjectQrSection>
          <ObjectQrHeader onClick={() => setIsQrListExpanded(!isQrListExpanded)}>
            <ObjectQrTitle>AR 오브젝트 QR 목록</ObjectQrTitle>
            <ToggleIcon $isExpanded={isQrListExpanded}>▼</ToggleIcon>
          </ObjectQrHeader>
          {isQrListExpanded && (
            <>
              <ObjectQrSubtitle>각 QR에는 오브젝트 이름이 JSON 형식으로 담겨 있으며, 방 코드와는 별도로 인식됩니다.</ObjectQrSubtitle>
              <ObjectQrGrid>
                {objectQrCodes.map((obj) => (
                  <ObjectQrCard key={`${obj.name}-${obj.id || 'none'}`}>
                    <ObjectPreview>
                      {obj.thumbnail ? (
                        <img src={obj.thumbnail} alt={`${obj.name} 이미지`} />
                      ) : (
                        <ObjectPreviewFallback>이미지 없음</ObjectPreviewFallback>
                      )}
                    </ObjectPreview>
                    <img src={obj.image} alt={`${obj.name} QR`} />
                    <ObjectQrName>{obj.name}</ObjectQrName>
                    {obj.thumbnail && (
                      <ObjectQrPayload>이미지: {obj.thumbnail}</ObjectQrPayload>
                    )}
                    <ObjectQrPayload>{obj.payload}</ObjectQrPayload>
                  </ObjectQrCard>
                ))}
              </ObjectQrGrid>
            </>
          )}
        </ObjectQrSection>
      )}

      {showRanking && (
        <GameRankingBoard players={getRankingData()} goal={200000} onClose={() => setShowRanking(false)} onGoalAchieved={handleGoalAchieved} />
      )}

      {showFinalRanking && (
        <FinalRankingBoard players={getRankingData()} onContinue={handleContinueToProblemDetail} />
      )}

      {showLogs && (
        <LogPanel>
          <LogHeader>
            <LogTitle>🔌 Socket.IO 로그</LogTitle>
            <LogControls>
              <LogButton onClick={clearLogs}>Clear</LogButton>
              <LogButton onClick={() => setShowLogs(false)}>✕</LogButton>
            </LogControls>
          </LogHeader>
          <LogContent ref={logContentRef}>
            {logs.length === 0 ? (
              <LogEntry $type="info">로그 대기 중...</LogEntry>
            ) : (
              logs.map((log, i) => (
                <LogEntry key={i} $type={log.type} $highlight={log.highlight}>
                  [{log.timestamp}] {log.message}
                </LogEntry>
              ))
            )}
          </LogContent>
        </LogPanel>
      )}

      {!showLogs && (
        <LogButton
          onClick={() => setShowLogs(true)}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '8px 16px',
            fontSize: '12px'
          }}
        >
          📊 로그 보기
        </LogButton>
      )}

    </Container>
  )
}
